import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { FileText, Loader2, Edit2, Save, X, AlertCircle } from 'lucide-react';
import { reachPlanAPI, ReachAdoptionPlan, ReachPlanSection } from '../../services/reachService';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export const AdoptionPlanPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [plan, setPlan] = useState<ReachAdoptionPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const load = useCallback(async () => {
    if (!projectId) return;
    const r = await reachPlanAPI.get(projectId);
    setPlan(r.data.plan);
  }, [projectId]);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const startEdit = (section: ReachPlanSection) => {
    setEditingId(section.id);
    setEditDraft(section.editedContent ?? section.content ?? '');
  };

  const saveEdit = async (sectionId: string) => {
    if (!projectId) return;
    setSaving(true);
    try {
      await reachPlanAPI.updateSection(projectId, sectionId, editDraft);
      setPlan(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          sections: prev.sections.map(s =>
            s.id === sectionId ? { ...s, editedContent: editDraft } : s
          )
        };
      });
      setEditingId(null);
    } finally {
      setSaving(false);
    }
  };

  if (!projectId) return null;

  const renderSectionBody = (section: ReachPlanSection, isEditing: boolean) => {
    const display = section.editedContent ?? section.content;
    if (isEditing) {
      return (
        <textarea
          rows={8}
          value={editDraft}
          onChange={e => setEditDraft(e.target.value)}
          className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
        />
      );
    }
    if (display) {
      return (
        <div className="prose prose-sm max-w-none prose-headings:text-navy prose-headings:font-bold prose-p:text-slate-700 prose-p:leading-relaxed prose-a:text-primary prose-strong:text-navy prose-table:text-sm prose-th:text-navy prose-th:font-semibold prose-th:bg-slate-50 prose-td:text-slate-700 prose-li:text-slate-700">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{display}</ReactMarkdown>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2 py-4 text-slate-400">
        <AlertCircle size={14} />
        <span className="text-sm">Not yet drafted.</span>
        {section.guidance && (
          <p className="ml-2 text-xs text-slate-400 italic">{section.guidance}</p>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <FileText size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Change Adoption Plan</h1>
            <p className="text-slate-500 text-sm font-medium">
              {plan
                ? `${plan.sections.filter(s => s.content || s.editedContent).length} of ${plan.sections.length} sections drafted`
                : 'Plan drafted from your REACH engagement data'}
            </p>
          </div>
        </header>

        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 size={24} className="animate-spin text-slate-300" />
          </div>
        )}

        {!loading && (!plan || plan.sections.length === 0) && (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <FileText size={28} className="text-slate-400" />
            </div>
            <p className="text-slate-500 text-sm">No plan drafted yet.</p>
            <p className="text-slate-400 text-xs mt-1">Drafted by the change management skill in Claude Code (/reach-plan-section).</p>
          </div>
        )}

        {!loading && plan && plan.sections.length > 0 && (
          <div className="flex flex-col gap-4">
            {plan.sections.map(section => {
              const isEditing = editingId === section.id;
              const display = section.editedContent ?? section.content;

              return (
                <div key={section.id} className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-[#e2e8f0] bg-slate-50/50 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <p className="text-sm font-bold text-navy truncate">{section.title}</p>
                      {!!section.editedContent && (
                        <span className="flex-shrink-0 flex items-center gap-1 text-[10px] text-primary font-semibold">
                          <Edit2 size={10} /> Edited
                        </span>
                      )}
                      {!section.hasSufficientData && (
                        <span className="flex-shrink-0 flex items-center gap-1 text-[10px] text-amber-600 font-semibold">
                          <AlertCircle size={10} /> Low data
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {display && !isEditing && (
                        <button
                          onClick={() => startEdit(section)}
                          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
                        >
                          <Edit2 size={12} /> Edit
                        </button>
                      )}
                      {isEditing && (
                        <>
                          <button
                            onClick={() => saveEdit(section.id)}
                            disabled={saving}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60"
                          >
                            <Save size={12} /> {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-100 transition-colors"
                          >
                            <X size={12} /> Cancel
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="p-4">
                    {renderSectionBody(section, isEditing)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdoptionPlanPage;
